<template>
  <b-form
    enctype="multipart/form-data"
    class="job-form form"
    autocomplete="off"
    @submit.prevent="onSubmit"
  >
    <div class="form__section">
      <div class="form__section">
        <h3 class="page-subtitle">Pictures</h3>
        <div class="preview-container" v-if="previews.length !== 0">
          <div
            :class="[
              'preview-item',
              {
                'mr-4': index !== previews.length - 1
              }
            ]"
            v-for="(preview, index) in previews"
            :key="index"
          >
            <b-icon-x-circle-fill
              variant="danger"
              class="remove-icon"
              @click="onRemovePreview(index, preview)"
            ></b-icon-x-circle-fill>

            <b-img-lazy
              :src="preview.url"
              alt="Photos"
              v-bind="previewProps"
              class="h-100"
            ></b-img-lazy>
          </div>
        </div>
        <div
          class="upload-container upload-container--auto align-items-center mb-4"
          v-cloak
          @drop.prevent="loadPhotos($event)"
          @dragover.prevent
        >
          <b-img
            :src="require('@/assets/images/upload.svg')"
            alt="Photos"
            width="60"
            center
            class="tool-placeholder mb-2 pt-2"
          ></b-img>
          <b-button
            variant="outline-primary"
            class="btn--upload upload-container-btn mb-2"
            @click="launchFilePicker"
            >Upload photo</b-button
          >
          <p class="pb-2 upload-container-text">Or drag them in</p>
          <error-message
            class="text-center"
            sign=""
            :error="form.$getError('images')"
          ></error-message>

          <b-form-file
            ref="filePicker"
            multiple
            accept="image/*"
            style="display:none"
            @input="loadPhotos($event)"
            aria-invalid="input-images-feedback"
          ></b-form-file>
        </div>
        <b-alert
          variant="success"
          show
          class="d-flex flex-row align-items-center mb-5"
        >
          <div>
            <b-img
              :src="require('@/assets/images/alert-tip.svg')"
              alt="Alert"
              width="35"
              class="mr-3"
            ></b-img>
          </div>
          <div>
            <p>
              Products with pictures are more successful! You can upload images
              up to 10MB.
            </p>
            <p>
              Drag and drop files onto this window to upload.
            </p>
          </div>
        </b-alert>
      </div>
    </div>
    <div class="form__section">
      <h3 class="page-subtitle">Details</h3>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.name"
              type="text"
              placeholder="Name"
              required
              autofocus
              :disabled="form.$busy"
              :is-invalid="form.$getError('name')"
            ></custom-input>
            <error-message :error="form.$getError('name')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.description"
              type="text"
              component="b-textarea"
              placeholder="Description"
              required
              :disabled="form.$busy"
              :is-invalid="form.$getError('description')"
            ></custom-input>
            <error-message
              :error="form.$getError('description')"
            ></error-message>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group>
            <custom-input
              v-model="form.price"
              type="text"
              required
              placeholder="Price"
              :disabled="form.$busy"
              :is-invalid="form.$getError('price')"
              class="text-right"
              data-type="currency"
              @keyup="formatCurrency($event)"
              @blur="formatCurrency($event, 'blur')"
            ></custom-input>
            <error-message :error="form.$getError('price')"></error-message>
          </b-form-group>
        </b-col>
      </b-row>

      <address-fields
        ref="addressFields"
        v-if="user"
        :form="form"
        :api-key="$env.placesApiKey"
        @loading="onAddressFieldLoading"
      ></address-fields>
    </div>
    <div class="form__section form__section--mt">
      <b-button type="submit" block variant="primary" :disabled="form.$busy">
        <b-spinner small v-if="form.$busy" class="mr-2"></b-spinner
        >{{ buttonLabel }}</b-button
      >
    </div>
  </b-form>
</template>
<script>
/**
 * ==================================================================================
 * Job form
 * ==================================================================================
 **/

import { mapState, mapActions } from 'vuex'
import Form from '@/utils/form'
import CustomInput from '@/components/inputs/CustomInput'
import ErrorMessage from '@/components/inputs/ErrorMessage'
import AddressFields from '@/components/autocomplete/AddressFields'
import Compressor from 'compressorjs'

const MAX_PHOTO = 5

export default {
  components: {
    CustomInput,
    ErrorMessage,
    AddressFields
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      job: state => state.job.jobDetails
    }),

    submit() {
      return this.isOwner ? this.updateJob : this.createJob
    },

    buttonLabel() {
      return this.isOwner ? 'Edit tool' : 'Add tool'
    },

    loadingLabel() {
      return this.isOwner ? 'Updating...' : 'Saving...'
    },

    afterLoadingLabel() {
      return (
        `${this.form.name} has been ` + (this.isOwner ? 'updated' : 'posted')
      )
    },

    willAutoFetchCoords() {
      if (!this.user || !this.$env.placesApiKey) return false
      return (
        (!this.form.latitude || !this.form.longitude) && this.form.address_1
      )
    },

    isOwner() {
      if (!this.job || !this.user) return
      return this.job.owner.id === this.user.id
    }
  },
  watch: {
    user(newVal) {
      this.reset()
    }
  },
  data: function() {
    return {
      previewProps: {
        center: true,
        blank: true
      },
      previews: [],
      form: new Form({
        permalink: null,
        name: null,
        description: null,
        price: null,
        images: [],
        address_1: null,
        city: null,
        postal_code: null,
        state: null,
        country: null,
        latitude: null,
        longitude: null
      })
    }
  },
  methods: {
    ...mapActions({
      createJob: 'job/createJob',
      updateJob: 'job/updateJob',
      uploadJobImage: 'job/uploadJobImage',
      removeJobImage: 'job/removeJobImage',
      addAlert: 'alert/addAlert'
    }),
    async onSubmit() {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      this.addAlert({
        state: true,
        label: this.loadingLabel,
        countdown: true
      })

      await this.submit(this.getFormData())
        .then(() => {
          this.$router.push({
            name: 'job.my-jobs-page',
            params: {
              alert: {
                label: this.afterLoadingLabel
              }
            }
          })
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.addAlert({
            state: false,
            label: this.form.$errorMessage
          })
        })
    },

    async onImageUpload(images) {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      this.addAlert({
        state: true,
        label: 'Uploading image...',
        countdown: true
      })

      await this.uploadJobImage({
        permalink: this.job.permalink,
        images: images
      })
        .then(() => {
          this.form.images = []
          this.form.$setLoading(false)
          this.addAlert({
            label: 'Image(s) has been uploaded'
          })
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.addAlert({
            state: false,
            label: this.form.$errorMessage
          })
        })
    },

    async onImageDelete(image) {
      if (this.form.$busy) return
      this.form.$setLoading(true)
      this.addAlert({
        state: true,
        label: 'Removing image...',
        countdown: true
      })

      await this.removeJobImage({ permalink: this.job.permalink, id: image.id })
        .then(() => {
          this.form.$setLoading(false)
          this.addAlert({
            label: 'Image has been removed'
          })
        })
        .catch(error => {
          this.form.$setLoading(false)
          this.form.$handleError(error)
          this.addAlert({
            state: false,
            label: this.form.$errorMessage
          })
        })
    },

    onAddressFieldLoading(bool) {
      this.form.$busy = bool
    },

    reset() {
      if (this.job) {
        for (let key in this.form) {
          if (this.job.hasOwnProperty(key)) {
            this.form[key] = this.job[key]

            if (key === 'price') {
              this.form.price = this.formatPrice(this.job.price, 'blur')
            }

            this.previews = this.job.gallery.map(p => {
              return { id: p.id, url: p.url, loading: false }
            })
          }
        }
      } else if (this.user) {
        this.form.address_1 = this.user.address_1
        this.form.city = this.user.city
        this.form.postal_code = this.user.postal_code
        this.form.state = this.user.state
        this.form.country = this.user.country
      }

      if (
        this.willAutoFetchCoords &&
        this.$refs.addressFields &&
        this.$refs.addressFields.isPopulating
      ) {
        this.form.$busy = this.$refs.addressFields.isPopulating
      }
    },

    getFormData() {
      if (!this.job) {
        let formData = this.form
        formData = new FormData()
        formData.append('id', this.form.id)
        formData.append('name', this.form.name)
        formData.append('description', this.form.description)
        formData.append('price', this.formatInteger(this.form.price))
        formData.append('address_1', this.form.address_1)
        formData.append('city', this.form.city)
        formData.append('postal_code', this.form.postal_code)
        formData.append('state', this.form.state)
        formData.append('country', this.form.country)

        if (formData.latitude && formData.longitude) {
          formData.append('latitude', this.form.latitude)
          formData.append('longitude', this.form.longitude)
        }

        for (let i = 0; i < this.form.images.length; i++) {
          formData.append('images[]', this.form.images[i])
        }

        return formData
      } else {
        let formData = this.form.$data()
        formData.price = this.formatInteger(formData.price)

        if (!formData.latitude || !formData.longitude) {
          delete formData.latitude
          delete formData.longitude
        }

        return formData
      }
    },

    /**
     * Photos!
     * ==================================================================================
     **/

    launchFilePicker() {
      this.$refs.filePicker.$el.childNodes[0].click()
    },

    addCompressedImage(result) {
      this.form.images.push(result)
    },

    loadPhotos(e) {
      let _this = this
      const images = e.dataTransfer ? e.dataTransfer.files : e

      if (images.length + this.previews.length > MAX_PHOTO) {
        this.addAlert({
          state: false,
          label: `Maximum of ${MAX_PHOTO} images only`
        })
      } else {
        let uploadCount = 0
        for (let i = 0; i < images.length; i++) {
          const file = images[i]
          const reader = new FileReader()
          reader.readAsDataURL(images[i])
          reader.onload = e => {
            const base64Img = e.target.result
            const binaryImg = this.convertDataURIToBinary(base64Img)
            const blob = new Blob([binaryImg], { type: file.type })

            if (file.size > 10000000) {
              this.addAlert({
                state: false,
                label: `Fail to upload ${file.name}, cannot upload images greater than 10mb`
              })
            } else if (file.size > 2000000) {
              // eslint-disable-next-line
              new Compressor(blob, {
                quality: 0.2,
                success(result) {
                  _this.addCompressedImage(result)
                }
              })

              this.previews.push({ url: base64Img, loading: false })
            } else {
              this.addCompressedImage(blob)
              this.previews.push({ url: base64Img, loading: false })
            }

            uploadCount++
            if (uploadCount === images.length && this.job) {
              let formData = new FormData()
              for (let i = 0; i < this.form.images.length; i++) {
                formData.append('images[]', this.form.images[i])
              }
              this.onImageUpload(formData)
            }
          }
        }
      }
    },

    convertDataURIToBinary(dataURI) {
      const BASE64_MARKER = ';base64,'
      const base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
      const base64 = dataURI.substring(base64Index)
      const raw = window.atob(base64)
      const rawLength = raw.length
      var array = new Uint8Array(new ArrayBuffer(rawLength))

      for (let i = 0; i < rawLength; i++) {
        array[i] = raw.charCodeAt(i)
      }
      return array
    },

    async onRemovePreview(index, image = null) {
      if (!this.job) {
        this.form.images.splice(index, 1)
      } else {
        await this.onImageDelete(image)
      }

      this.previews.splice(index, 1)
    },

    /**
     * Price/Currency!
     * ==================================================================================
     **/

    formatCurrency(e, blur) {
      let value = e.target.value
      if (value === '' || value === null) return

      let originalLen = value.length
      let caretPos = e.target.selectionStart
      let newValue = this.formatPrice(value, blur)
      e.target.value = newValue === '.00' ? '' : newValue
      caretPos = newValue.length - originalLen + caretPos

      /**
       * !@NOTE: Safari not allowing to focus on any other fields
       * once focused was on the price field
       *
       * https://stackoverflow.com/questions/3272089/programmatically-selecting-text-in-an-input-field-on-ios-devices-mobile-safari
       */
      setTimeout(() => {
        e.target.setSelectionRange(caretPos, caretPos)
      }, 1)

      this.form.price = e.target.value
    },

    formatPrice(n, hasDecimal = null) {
      let value = n.toString()
      if (value === '' || value === null) return

      const decimalPos = value.indexOf('.')
      value = value.replace(/^\$0+/, '')
      if (decimalPos >= 0) {
        value =
          `${this.formatNumber(value.substring(0, decimalPos))}.` +
          (
            this.formatNumber(value.substring(decimalPos)) +
            (hasDecimal === 'blur' ? '00' : '')
          ).substring(0, 2)
      } else {
        value = `${this.formatNumber(value)}`
        if (hasDecimal === 'blur') {
          value += '.00'
        }
      }

      return value
    },

    formatInteger(n) {
      if (!n) return
      return n.toString().replace(/,/g, '')
    },

    formatNumber(n) {
      if (!n) return
      return n
        .toString()
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    }
  },
  created() {
    this.reset()
  }
}
</script>
<style scoped lang="scss">
.job-form {
  .form__section:first-child {
    .page-subtitle {
      padding-top: 50px;
    }
  }

  .preview-container {
    .preview-item {
      width: 125px;
      height: 125px;
    }
  }

  .upload-container {
    img {
      height: auto;
    }

    .btn {
      width: 195px;
    }

    p {
      font-size: 0.95rem;
      color: #5d5d5d;
    }
  }
}
</style>
